import type { ImportPmsVoterRow } from '@api/pms-voters-api';
import { escapeCsvField, parseCsvHeaders, splitCsvLine } from '@utils/csv-utils';

/**
 * CSV parsing for the PMS voter census.
 *
 * The file uses the HubSpot contact export columns as-is, so a list pulled from
 * HubSpot imports without renaming: `Record ID - Contact` (the contact id, stored
 * as the voter's HubSpot id), `Email`, and the `Awards Voting Token` column
 * HubSpot writes back to. The token column is ignored on import — the server
 * issues the token — and filled by the export.
 *
 * Nominees are not here: they go through the edition importer, which owns
 * editions, categories and nominations for every site.
 */

// HubSpot headers, matched case-insensitively. The "Awards Voting Token" column
// (which HubSpot annotates as " [Upload] [Temporary]") is not read on import: the
// server issues the token and the export fills that column.
const VOTER_RECORD_ID = 'record id - contact';
const VOTER_EMAIL = 'email';

function readRows(content: string): { headers: string[]; rows: string[][] } {
  const lines = content.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) return { headers: [], rows: [] };

  const { headers, delimiter } = parseCsvHeaders(lines[0]!);
  const rows = lines.slice(1).map((line) => splitCsvLine(line, delimiter));

  return { headers: headers.map((h) => h.trim().toLowerCase()), rows };
}

function cell(headers: string[], row: string[], name: string): string {
  const index = headers.indexOf(name);
  return index === -1 ? '' : (row[index] ?? '').trim();
}

export function validatePmsVoterHeaders(content: string): string | null {
  const { headers } = readRows(content);
  const missing: string[] = [];
  if (!headers.includes(VOTER_RECORD_ID)) missing.push('Record ID - Contact');
  if (!headers.includes(VOTER_EMAIL)) missing.push('Email');
  return missing.length > 0 ? `Missing required column(s): ${missing.join(', ')}` : null;
}

export function parsePmsVoterRows(content: string): ImportPmsVoterRow[] {
  const { headers, rows } = readRows(content);

  return rows
    .map((row) => ({
      email: cell(headers, row, VOTER_EMAIL),
      hsid: cell(headers, row, VOTER_RECORD_ID),
    }))
    .filter((row) => row.email !== '' || row.hsid !== '');
}

export function buildFailedPmsRowsCsv(
  failedRows: readonly { row: Record<string, unknown>; reason: string }[],
): string {
  if (failedRows.length === 0) return '';

  const columns = [...new Set(failedRows.flatMap((f) => Object.keys(f.row)))];
  const header = [...columns, 'reason'].map(escapeCsvField).join(',');

  const lines = failedRows.map((failed) =>
    [...columns.map((c) => String(failed.row[c] ?? '')), failed.reason]
      .map(escapeCsvField)
      .join(','),
  );

  return [header, ...lines].join('\n');
}
